import { Component, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { v4 as uuid4 } from 'uuid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AnnotationService,
  onCancelAddingAnnotation,
  onCommentsVisibilityChange,
  onMediaDownloadClick,
  onPluginReady,
} from '../../services/annotation.service';
import { AuthService } from '../../services/auth.service';
import { commentFileAdd } from '../../services/comments.service';
import { AnnotationPrivateCondition, AnnotationSendOtherTypesMessage } from '../../interfaces';
import { EmitterService } from '../../services/emitter.service';
import { MTMFileDownloadControl } from '../mtm-file-download/mtm-file-download-control/mtm-file-download-control.component';
import { CommentAnnotationPlugin } from './plugin/comment-annotation.plugin';
import {
  MtmStompService,
  wsListenerANNOTATION_CREATED,
} from '../../services/mtm-stomp.service';

@Component({
  selector: 'old-mtm-comment-annotation',
  styleUrl: './old-comment-annotation.component.scss',
  templateUrl: './old-comment-annotation.component.html',
  standalone: false,
})
export class OldCommentAnnotation implements OnInit, OnDestroy, AfterViewInit {
  @Input() projectId: any;
  @Input() sectionId: any;
  @Input() subsectionId: any;
  @Input() item: any;
  @Input() annotations: any[];
  @Input() hideAnnotationToolbar: boolean = true;
  @Input() hideTopMediaInfo: boolean = false;
  @Input() instanceId: string;
  annotationPlugin: CommentAnnotationPlugin;
  authUser: any;
  annotationComment: string = '';
  pluginReady: any;
  wsId: string;
  newFiles: any = [];

  files: any = [];
  videoRecordFiles: any = [];
  audioRecordFiles: any = [];
  privateCondition: AnnotationPrivateCondition = null;
  ngUnsubscribe = new Subject();
  isBusy: boolean = false;
  annotatingMode: boolean = false;
  @ViewChild('downloadButton') downloadButton: MTMFileDownloadControl;
  constructor(
    private readonly stompService: MtmStompService,
    private readonly authService: AuthService,
    private readonly annotationService: AnnotationService
  ) {}

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (value: AnnotationPrivateCondition) => {
        this.privateCondition = value;
      },
    });
    // window["com"] = this;
    this.initPlugin();

    EmitterService.get(AnnotationSendOtherTypesMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (this.instanceId != data.instanceId) {
            return;
          }
          if (data.message) {
            this.annotationComment = data.message;
            this.newFiles = data.newFiles;
            this.addNewAnnotation();
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.stompService.unsubscribeToService(this.wsId);
  }

  ngAfterViewInit(): void {
    this.wsId = this.stompService.subscribeToService();
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    onCancelAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCancelAddingAnnotationHandler(args),
    });
    onCommentsVisibilityChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.applyAnnotationsDataChange(args),
    });
    this.stompService.subscribeToListener(
      'ANNOTATION_CREATED',
      this.wsId,
      [this.projectId, this.sectionId, this.subsectionId],
      (args: any) => this.wsOnAnnotationCreate(args),
      wsListenerANNOTATION_CREATED
    );

    onMediaDownloadClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        console.log('onMediaDownloadClick', args);
        if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
          return;
        }
        this.downloadAsset();
      },
    });
  }

  wsOnAnnotationCreate(args) {
    commentFileAdd.emit({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  downloadAsset() {
    this.downloadButton.downloadFile();
  }

  addNewAnnotation() {
    this.annotationService.updateCommentListVisibility(this.privateCondition);
    if ((!this.annotationComment && !this.newFiles?.length) || this.isBusy) {
      return;
    }
    if (this.annotationPlugin && this.pluginReady) {
      this.annotationPlugin.fire('saveGeneralComment');
    }
  }

  isInValidInput() {
    return !this.annotationComment;
  }

  initPlugin() {
    const config = {
      userInfo: this.authUser,
      item: this.item,
      entityName: 'FILE',
      annotationObjects: this.annotations,
      instanceId: this.instanceId,
    };
    this.annotationPlugin = new CommentAnnotationPlugin(this, config);

    this.annotationPlugin.onReady(() => {
      this.pluginReady = true;
      onPluginReady.emit({
        type: 'comment',
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item.id,
      });
    });
  }

  onCancelAddingAnnotationHandler(args: any) {
    if (this.instanceId === args.instanceId && this.item.id === args.itemId) {
      this.resetUIComponent();
    }
  }

  startAnnotationMode() {
    if (this.pluginReady && this.annotationPlugin) {
      this.annotatingMode = true;
    }
  }
  resetUIComponent() {
    this.annotationComment = '';
    this.isBusy = false;
    this.annotatingMode = false;
  }

  /** Gets the comment input place holder text depending on general or annot comment */
  getPlaceholderText() {
    // let placeholderText;
    // if ( this.annotationPlugin && this.annotationPlugin.controls && (this.annotationPlugin.controls.uiState.adding || this.annotationPlugin.controls.uiState.editing) ){
    // 	placeholderText = "Write an annotation comment..."
    // }
    // else {
    // 	placeholderText = "Write a general comment..."
    // }

    const placeholderText = 'Write a general comment...';

    return placeholderText;
  }

  cleanUp() {
    this.resetUIComponent();
    this.cleanUpFiles();
  }

  cleanUpFiles() {
    this.newFiles = [];
    this.files = [];
    this.videoRecordFiles = [];
    this.audioRecordFiles = [];
  }

  uploadFileCallback(fileObject) {
    const { files } = fileObject;
    if (!files) {
      return;
    }
    files.map((file) => {
      if (!file.fileLocalId) {
        file.fileLocalId = uuid4();
      }
      return file;
    });
    files.forEach((file) => {
      if (file.method == 'audioRecord') {
        this.audioRecordFiles.push(file);
      } else if (file.method == 'videoRecord') {
        this.videoRecordFiles.push(file);
      } else {
        this.files.push(file);
      }
    });
    this.newFiles.push(...files);
  }

  onFileDeleted(file) {
    this.newFiles = this.newFiles.filter((f) => f.lastModified != file.lastModified);
    this.audioRecordFiles = this.newFiles.filter((f) => f.method == 'audioRecord');
    this.videoRecordFiles = this.newFiles.filter((f) => f.method == 'videoRecord');
    this.files = this.newFiles.filter((f) => !f.method);
  }

  handlePrivateConditionChange(value: AnnotationPrivateCondition) {
    this.annotationService.updatePrivateCondition(value);
  }

  applyAnnotationsDataChange(args: any) {
    if (args.data && this.instanceId === args.instanceId && this.item.id === args.itemId) {
      this.annotationPlugin.annotationState.annotations = args.data;
    }
  }
}
